<script setup lang="ts">
const props = defineProps({
  item: {
    type: Object as PropType<Item>,
    required: true,
  },
});
const isHovered = ref<boolean>(false);

const flexPosition = computed(() => {
  if (props.item.end && props.item.oneTime) {
    return "justify-between";
  } else {
    return "justify-center";
  }
});

const calculateFontSize = (text: string): string => {
  const maxLength = 12;

  if (text.length <= maxLength) return `text-4xl`;
  return `text-[1.9rem]`;
};
</script>

<template>
  <div
    class="h-full gradient relative grid grid-rows-[1fr,1fr,1fr, 1fr] border-2 px-3 py-4 will-change-transform transition ease-in-out duration-300"
    :class="[
      ` ${getItemSpecialColor(item.id, 'border')} rounded-lg `,
      isHovered && '-translate-y-1',
    ]"
    @mouseover="isHovered = true"
    @mouseleave="isHovered = false">
    <div
      :class="isHovered && 'opacity-50'"
      class="overlay absolute inset-0 bg-neutral-800 rounded-lg opacity-0 transition-opacity duration-300" />
    <div
      class="relative w-full flex flex-col items-center justify-center gap-1 truncate">
      <div>
        <h1
          v-if="item.oneTime"
          class="h-5 rounded-full text-neutral-500 text-xs font-bold flex items-center px-4"
          :class="`${getItemSpecialColor(item.id, 'bg')}`">
          {{ $t(`${item.translationId}.offer`) }}
        </h1>
        <h1
          v-else-if="
            item.originalPrice?.GFAL &&
            item.originalPrice?.GFAL !== item.price?.GFAL &&
            item.id === 'shop_item_0006'
          "
          class="h-5 rounded-full text-neutral-500 text-xs font-bold flex items-center px-4"
          :class="`${getItemSpecialColor(item.id, 'bg')}`">
          {{ $t(`${item.translationId}.offer`) }}
        </h1>
        <h1
          v-else-if="
            item.originalPrice?.GFAL &&
            item.originalPrice?.GFAL !== item.price?.GFAL &&
            item.id === 'shop_item_0060'
          "
          class="h-5 rounded-full text-neutral-500 text-xs font-bold flex items-center px-4"
          :class="`${getItemSpecialColor(item.id, 'bg')}`">
          {{ $t(`${item.translationId}.offer`) }}
        </h1>
        <div v-else class="h-4" />
      </div>
      <div
        :class="isHovered && 'scale-110'"
        class="max-w-full mt-1 transition-transform ease-in-out duration-500 h-10 flex items-center truncate px-2">
        <h1
          class="text-neutral-200 font-bold truncate max-w-full"
          :class="calculateFontSize($t(`${item.translationId}.title`))">
          {{ $t(`${item.translationId}.title`) }}
        </h1>
      </div>
    </div>
    <div
      class="w-full h-full transition-transform ease-in-out duration-500 will-change-transform"
      :class="isHovered && 'scale-110'">
      <CardImage :src="getPortraitItemImage(item.imageId)" />
    </div>
    <div
      v-if="item.price"
      class="relative mt-3 flex flex-col justify-center items-center gap-1 px-4 shadow-xs focus-within:ring-2 focus-within:ring-indigo-500 focus-within:ring-offset-2">
      <div class="flex gap-2">
        <BaseIcon
          name="GFAL_token"
          class="w-7"
          :alt="$t('main.imageAltTexts.GFALTokenLogo')" />
        <p class="text-2xl text-neutral-200 font-bold">
          {{ roundNum(item.price.GFAL) }}
        </p>
      </div>
      <div class="flex items-center justify-center gap-2">
        <p class="text-neutral-300">
          {{ "$" + roundNum(item.price.USD) }}
        </p>
        <p
          v-if="
            item.originalPrice?.USD &&
            item.originalPrice?.USD !== item.price?.USD
          "
          class="text-neutral-200 font-semibold bg-error-300 px-2 rounded-br-2xl rounded-tr rounded-bl rounded-tl-2xl">
          -{{ calculateDiscount(item.originalPrice.USD, item.price.USD) }}%
        </p>
      </div>
    </div>
    <div class="mt-3 px-3 flex" :class="flexPosition">
      <div v-if="item.oneTime">
        <div
          v-if="item.purchased"
          class="border border-rarity-exotic rounded-sm px-2 py-1 flex items-center gap-1">
          <p class="text-rarity-exotic text-xs font-semibold">Purchased</p>
          <BaseIcon name="hexagon_check" class="w-4 fill-rarity-exotic" />
        </div>
        <div v-else class="border border-success-300 rounded-sm px-2 py-0.5">
          <p class="text-success-300 text-xs font-semibold">Available: 1/1</p>
        </div>
      </div>
      <div v-if="item.end" class="text-xs flex items-center gap-1">
        <NuxtIcon name="mdi:clock-outline" class="text-primary-500" />
        <p class="text-secondary-200">Ends in:</p>
        <CommonCountdown
          :datetime="item.end"
          text-style="abbreviated"
          class="text-neutral-200 font-semibold" />
      </div>
    </div>
  </div>
</template>

<style scoped>
.gradient {
  background: radial-gradient(
    90% 96.97% at 50% 50%,
    #434343f5 0%,
    #0c0c0c 71.92%
  );
}
</style>
